import { useEffect, useMemo, useState } from 'react';
import {
    ColumnDef,
    Row,
    getCoreRowModel,
    getFilteredRowModel,
    getPaginationRowModel,
    getSortedRowModel,
    useReactTable,
} from '@tanstack/react-table';
import { SORTING_FNS } from 'plugins/DITable/helpers/sort';
import { Box, Text, useDisclosure } from '@chakra-ui/react';

import DefaultColumnFilter from 'plugins/DITable/filters/DefaultColumnFilter';
import { linkStyle } from 'shared/styles';
import { useLazyGetAllClientProductsQuery } from 'core/API/productsApi';
import { IProductTitle, TProductSplit, TRetailerProp } from 'shared/types';
import { useGetCompanyInfoSelector, useGetUserInfoSelector } from 'core/redux/selectors/authSelectors';
import { useTranslation } from 'helpers/languages/translations';

import { parseProductsAliases } from './helper';
import { emptyProductTitle } from './consts';
import { isUpcEnabled } from 'shared';

const useProductTitleContainer = () => {
    const translate = useTranslation();
    const companyInfo = useGetCompanyInfoSelector();

    const { isOpen, onOpen, onClose } = useDisclosure();
    const { isOpen: isTagOpen, onOpen: onTagOpen, onClose: OnTagClose } = useDisclosure();
    const { isOpen: isDeleteOpen, onOpen: onDeleteOpen, onClose: onDeleteClose } = useDisclosure();

    const [getClientProducts, { data: clientProducts, isError, isLoading, isFetching }] =
        useLazyGetAllClientProductsQuery();

    const [productToUpdate, setProductToUpdate] = useState<IProductTitle>(emptyProductTitle);
    const [modalType, setModalType] = useState<'add' | 'update'>('add');
    const [retailer, setRetailer] = useState<TRetailerProp>('default');
    const [lang, setLang] = useState<string>('default');

    const isUpc = isUpcEnabled(companyInfo);

    const userInfo = useGetUserInfoSelector();
    const readonly = !userInfo?.user_groups?.includes(20);

    useEffect(() => {
        const getData = async () => {
            await getClientProducts({
                retailer: retailer === 'default' ? 'default' : `${retailer.id}`,
                lang,
                isBrut: false,
                isAliases: true,
                isProductTitle: true,
            });
        };

        getData().catch(console.error);
    }, [retailer, lang]);

    const data: Readonly<IProductTitle[]> = useMemo(() => {
        if (!isLoading && !isError && clientProducts) {
            return parseProductsAliases(clientProducts.products, isUpc);
        }

        return [];
    }, [clientProducts, isError, isLoading]);

    const columns: Readonly<ColumnDef<IProductTitle, any>[]> = useMemo(() => {
        const handleClickProduct = (product: IProductTitle) => {
            setModalType('update');
            setProductToUpdate(product);
            onOpen();
        };

        const createdColumns: (ColumnDef<IProductTitle, any> | null)[] = [
            {
                header: () => (isUpc ? 'UPC' : 'EAN'),
                accessorKey: isUpc ? 'upc' : 'ean',
                cell: (info) => {
                    const splitRow = info.row.original as IProductTitle;
                    return (
                        <Box onClick={() => handleClickProduct(splitRow)} {...linkStyle}>
                            {info.getValue()}
                        </Box>
                    );
                },
            } as ColumnDef<IProductTitle, any>,
            {
                header: () => translate('product.name') as string,
                accessorKey: 'name',
                meta: {
                    minWidth: '300px',
                    width: '20%',
                },
            } as ColumnDef<IProductTitle, any>,
        ];

        if (!isLoading && !isError && clientProducts) {
            clientProducts.splitsTitles.forEach((split, idx) => {
                createdColumns.push({
                    header: () =>
                        /name_split/.test(split)
                            ? (translate('product.name_split_i', { idx: split.split('_').pop() }) as string)
                            : split,
                    accessorKey: `name_split_${idx + 1}`,
                    cell: (info) => <Text>{(info.getValue() as TProductSplit)?.val}</Text>,
                    // add a custom filter for nested columns
                    filterFn: (row, columnId, filterValue) => {
                        const splitValue = row.original[split as keyof IProductTitle] as TProductSplit | undefined;
                        const objectValue: string | undefined = splitValue?.val;
                        return objectValue?.toLowerCase().includes(String(filterValue)?.toLowerCase());
                    },
                } as ColumnDef<IProductTitle, any>);
            });
        }

        const filteredColumns = createdColumns.filter((e): e is ColumnDef<IProductTitle, any> => e !== null);
        return filteredColumns;
    }, [isLoading, isError, clientProducts]);

    const defaultColumn = useMemo(
        () => ({
            meta: {
                width: '150px',
                Filter: DefaultColumnFilter,
            },
        }),
        [],
    );

    const customGlobalFilter = (row: Row<IProductTitle>, columnId: string, filterValue: string) => {
        const rowValues: (string | TProductSplit | undefined)[] = Object.values(row.original);
        return rowValues.some((rowValue) => {
            if (typeof rowValue === 'string') {
                return rowValue.toLowerCase().includes(String(filterValue).toLowerCase());
            }
            if (typeof rowValue === 'object' && typeof rowValue?.val === 'string') {
                return rowValue.val.toLowerCase().includes(String(filterValue).toLowerCase());
            }
            return false;
        });
    };

    const table = useReactTable<IProductTitle>({
        data: data as IProductTitle[],
        columns: columns as ColumnDef<IProductTitle, any>[],
        // using a custom global filter because not all columns have the same structure
        globalFilterFn: customGlobalFilter,
        defaultColumn,
        sortingFns: SORTING_FNS,
        getCoreRowModel: getCoreRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
    });

    const props = useMemo(
        () => ({
            table,
            isLoading,
            isFetching,
            modalType,
            productToUpdate,
            isOpen,
            retailer,
            lang,
            isTagOpen,
            isDeleteOpen,
            clientProducts,
            isUpc,
            readonly,
            onOpen,
            onClose,
            onTagOpen,
            OnTagClose,
            onDeleteOpen,
            onDeleteClose,
            setProductToUpdate,
            setRetailer,
            setLang,
            setModalType,
        }),
        [
            table,
            isLoading,
            isFetching,
            modalType,
            productToUpdate,
            isOpen,
            retailer,
            lang,
            isTagOpen,
            isDeleteOpen,
            clientProducts,
            isUpc,
            readonly,
        ],
    );

    return props;
};

export type TProductTitleProps = ReturnType<typeof useProductTitleContainer>;

export default useProductTitleContainer;
